
import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuthStore } from "@/store/authStore";
import { isSuperAdmin } from "@/utils/roleUtils";
import { useNavigate } from "react-router-dom";
import { SuperAdminBrandSettings } from "@/components/super-admin/SuperAdminBrandSettings";
import { Store, ShoppingBag, Users, Palette } from "lucide-react";
import StoreManagement from "@/pages/StoreManagement";
import OrdersManagement from "@/pages/OrdersManagement";
import StaffManagement from "@/pages/StaffManagement";
import { useToast } from "@/hooks/use-toast";

const SuperAdminView = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const isSuperAdminUser = isSuperAdmin(user?.role);
  
  useEffect(() => {
    // For debugging - log user role to help identify permission issues
    console.log("Current user role:", user?.role);
    console.log("Is super admin:", isSuperAdminUser);
    
    // Redirect non super admin users
    if (!isSuperAdminUser) {
      toast({
        title: "Access Denied",
        description: "You need super admin privileges to access this page.",
        variant: "destructive"
      });
      navigate("/dashboard");
    } else {
      setLoading(false);
    }
  }, [isSuperAdminUser, navigate, user?.role, toast]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isSuperAdminUser) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Super Admin Management</h1>
        <p className="text-muted-foreground mt-2">
          Manage company-wide settings, stores, orders, and staff.
        </p>
      </div>
      
      <Tabs defaultValue="brand" className="w-full">
        <TabsList className="w-full md:w-auto grid grid-cols-2 md:grid-cols-4 h-auto gap-4 bg-transparent p-0">
          <TabsTrigger 
            value="brand" 
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center gap-2"
          >
            <Palette className="h-4 w-4" />
            <span>Brand</span>
          </TabsTrigger>
          <TabsTrigger 
            value="stores" 
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center gap-2"
          >
            <Store className="h-4 w-4" />
            <span>Stores</span>
          </TabsTrigger>
          <TabsTrigger 
            value="orders" 
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center gap-2"
          >
            <ShoppingBag className="h-4 w-4" />
            <span>Orders</span>
          </TabsTrigger>
          <TabsTrigger 
            value="staff" 
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground flex items-center gap-2"
          >
            <Users className="h-4 w-4" />
            <span>Staff</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="brand" className="mt-6">
          <SuperAdminBrandSettings />
        </TabsContent>
        
        <TabsContent value="stores" className="mt-6">
          <StoreManagement />
        </TabsContent>
        
        <TabsContent value="orders" className="mt-6">
          <OrdersManagement />
        </TabsContent>
        
        <TabsContent value="staff" className="mt-6">
          <StaffManagement />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SuperAdminView;
